import * as Notifications from 'expo-notifications';
import type { SQLiteDatabase } from 'expo-sqlite';
import { Platform } from 'react-native';

/**
 * Daily / weekly digest notifications via expo-notifications.
 *
 * Native: request permission, schedule a repeating local notification at the
 * user's chosen weekday + hour (weekly) or just hour (daily). Each fire body
 * is static for now — a future enhancement can re-fetch the next task at
 * boot via a background task and update the scheduled content.
 *
 * Web: no scheduling (browser Notifications API needs a service worker).
 */

export const NOTIFICATIONS_AVAILABLE = Platform.OS !== 'web';

/**
 * CRITICAL: expo-notifications suppresses notifications when the app is in
 * the foreground by default. Without this handler, tapping "Send a test
 * notification" (which fires while the app is obviously open) produces
 * nothing visible on Android/iOS — the OS receives the payload but renders
 * no banner, no sound, no anything.
 *
 * Installing the handler at module load means the very first `scheduleNot-
 * ificationAsync` call already has the right policy in place. iOS and
 * Android both honor `shouldShowBanner` / `shouldShowList` in SDK 53+;
 * older clients fall back to the deprecated `shouldShowAlert` field which
 * we also set for safety.
 */
if (Platform.OS !== 'web') {
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldShowBanner: true,
      shouldShowList: true,
      shouldPlaySound: true,
      shouldSetBadge: false,
    }),
  });
}

/**
 * Android 8+ (API 26+) silently drops any notification that isn't bound
 * to a registered channel. The `expo-notifications` plugin doesn't create
 * one for you — we have to do it at runtime, ideally before the first
 * `scheduleNotificationAsync` call. iOS and web no-op.
 *
 * Idempotent: `setNotificationChannelAsync` upserts by id, so calling it
 * multiple times is fine. We still guard with a module flag to avoid the
 * round-trip on every fire.
 */
let androidChannelEnsured = false;
export async function ensureAndroidNotificationChannel(): Promise<void> {
  if (Platform.OS !== 'android') return;
  if (androidChannelEnsured) return;
  try {
    await Notifications.setNotificationChannelAsync('default', {
      name: 'Homestead',
      importance: Notifications.AndroidImportance.DEFAULT,
      sound: 'default',
      vibrationPattern: [0, 250, 250, 250],
      lightColor: '#FBF6EA',
    });
    androidChannelEnsured = true;
  } catch (err) {
    console.warn('ensureAndroidNotificationChannel failed', err);
  }
}

/**
 * Web notification support is intentionally narrow: we can show an
 * immediate notification (e.g. "Here's your weekly digest") via the
 * browser Notification API when permission is granted, but we CANNOT
 * schedule future notifications — that needs a server-side push service
 * (Web Push + service worker), which is out of scope for the local-first
 * build. Native (iOS/Android) supports both.
 */
export const WEB_NOTIFICATIONS_AVAILABLE = (() => {
  if (Platform.OS !== 'web') return false;
  // eslint-disable-next-line no-undef
  return typeof window !== 'undefined' && 'Notification' in window;
})();

let swRegistration: ServiceWorkerRegistration | null = null;
let swRegistrationAttempted = false;

/**
 * Register the homestead service worker on web. Called once at app boot
 * (RootLayout). Failing to register is non-fatal — we fall back to
 * inline `new Notification(...)` if the SW isn't available, which still
 * works on most browsers but doesn't persist in the OS notification
 * center.
 */
export async function ensureServiceWorkerRegistered(): Promise<void> {
  if (Platform.OS !== 'web') return;
  if (swRegistrationAttempted) return;
  swRegistrationAttempted = true;
  // eslint-disable-next-line no-undef
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    // eslint-disable-next-line no-undef
    swRegistration = await navigator.serviceWorker.register('/sw.js');
  } catch (err) {
    // Browser blocked it (e.g. insecure context, file:// origin). Not fatal.
    console.warn('SW registration failed', err);
  }
}

export async function requestWebNotificationPermission(): Promise<boolean> {
  if (!WEB_NOTIFICATIONS_AVAILABLE) return false;
  // eslint-disable-next-line no-undef
  if (Notification.permission === 'granted') return true;
  // eslint-disable-next-line no-undef
  if (Notification.permission === 'denied') return false;
  // eslint-disable-next-line no-undef
  const res = await Notification.requestPermission();
  return res === 'granted';
}

/**
 * Cross-platform permission status. Maps both native (Expo) and web
 * (browser Notification API) to a single set of states the settings
 * UI can render uniformly.
 *
 *   • `granted`        — user has approved; notifications can fire
 *   • `denied`         — user explicitly blocked; on iOS / Android we
 *                        can no longer prompt and must direct them to
 *                        the OS Settings app
 *   • `not-requested`  — initial state on a fresh install; tapping the
 *                        Enable button will trigger the OS prompt
 *   • `unavailable`    — platform doesn't support notifications at all
 *                        (e.g. Safari on iOS web in some configurations)
 */
export type NotificationPermissionStatus =
  | 'granted'
  | 'denied'
  | 'not-requested'
  | 'unavailable';

export async function getNotificationPermissionStatus(): Promise<NotificationPermissionStatus> {
  if (Platform.OS === 'web') {
    if (!WEB_NOTIFICATIONS_AVAILABLE) return 'unavailable';
    // eslint-disable-next-line no-undef
    const p = Notification.permission;
    if (p === 'granted') return 'granted';
    if (p === 'denied') return 'denied';
    return 'not-requested';
  }
  // Native: Expo's getPermissionsAsync returns canAskAgain so we can
  // distinguish "haven't asked yet" from "user said no, settings app
  // required."
  const settings = await Notifications.getPermissionsAsync();
  if (settings.granted) return 'granted';
  if (settings.canAskAgain === false) return 'denied';
  return 'not-requested';
}

/**
 * Unified prompt — works on web + native. Returns the resulting status
 * (not just a boolean) so the caller can distinguish "user said no but
 * could ask again later" from "user blocked permanently, send them to
 * Settings."
 */
/**
 * Fire a one-shot confirmation notification on native. Used by the
 * notifications settings page right after the user grants permission
 * so they see notifications actually work end-to-end instead of just
 * trusting a green badge. Web has its own equivalent via
 * `showImmediateWebNotification`.
 *
 * Pass `withSound: false` to fire silently (visual + vibration only).
 * Per-notification sound override matches the per-category opt-outs
 * users can set in /notifications.
 */
export async function fireConfirmationNotification(
  withSound: boolean = true
): Promise<void> {
  if (!NOTIFICATIONS_AVAILABLE) return;
  try {
    await ensureAndroidNotificationChannel();
    await Notifications.scheduleNotificationAsync({
      content: {
        title: 'Homestead — notifications on',
        body: "We'll send the occasional nudge. Nothing naggy.",
        sound: withSound ? 'default' : false,
      },
      trigger: null, // fire immediately
    });
  } catch (err) {
    console.warn('Confirmation notification failed', err);
  }
}

/**
 * Schedule a one-time local notification ~24h after onboarding
 * completion: "Got 2 minutes to tune your plan?" with a deep link
 * to /profile-setup so the user lands directly on the walkthrough.
 *
 * Why 24h specifically:
 *   - Long enough that the user has actually used the app a bit and
 *     isn't getting nagged immediately after finishing setup.
 *   - Short enough that they still remember signing up (this isn't
 *     a re-engagement ping at week 3 from cold).
 *   - Aligns with how Duolingo / Headspace / etc. structure their
 *     first nudge — "day 1 → day 2" retention window.
 *
 * Idempotency: we use a stable identifier per property. If onboarding
 * is re-run (rare) the second schedule replaces the first — no
 * double-firing.
 *
 * Web: no-op. expo-notifications doesn't support reliable scheduled
 * notifications on web (would require a service worker push server).
 * The weekly inbox-prompt loop covers web users.
 *
 * Off-by-permission: silently no-op if the user hasn't granted
 * notification permission yet. We never auto-prompt for permission
 * from here — that's reserved for the user-initiated flow in the
 * notification settings screen.
 */
export async function schedulePostOnboardingNudge(
  propertyId: string,
  opts?: {
    /** When false, never pop the OS permission dialog — schedule only
     *  if permission is already granted. Used by the onboarding
     *  cadence step's skip path: the user just declined to pick a
     *  cadence, so surprising them with a permission dialog on the
     *  way out would be exactly the nagging we promise not to do. */
    promptIfNeeded?: boolean;
  }
): Promise<void> {
  if (!NOTIFICATIONS_AVAILABLE) return;
  try {
    // Prompt for permission if we don't already have it (unless the
    // caller opted out). The onboarding cadence step is the primary
    // prompt point — the user just told us WHEN they want to hear
    // from us, so the OS dialog lands with full context.
    const permission =
      opts?.promptIfNeeded === false
        ? await getNotificationPermissionStatus()
        : await requestNotificationPermissionUnified();
    if (permission !== 'granted') return;

    await ensureAndroidNotificationChannel();

    const identifier = `homestead-onboarding-tune-${propertyId}`;
    // Cancel any prior schedule for this property (safe no-op if none
    // exists). Lets a user who re-runs onboarding reset the timer.
    await Notifications.cancelScheduledNotificationAsync(identifier).catch(() => {});

    const fireAt = Date.now() + 24 * 60 * 60 * 1000;
    await Notifications.scheduleNotificationAsync({
      identifier,
      content: {
        title: 'Got 2 minutes for Homestead?',
        body:
          "Your plan's up and running. Answer a couple of chip questions to sharpen it for your home.",
        sound: 'default',
        // The data payload is what the response listener inspects when
        // the user taps the notification. `route` tells the app where
        // to deep-link. Convention-only — the listener has to read it.
        data: { kind: 'onboarding-tune', route: '/profile-setup' },
      },
      trigger: {
        type: Notifications.SchedulableTriggerInputTypes.DATE,
        date: new Date(fireAt),
      },
    });
  } catch (err) {
    console.warn('Post-onboarding nudge schedule failed', err);
  }
}

/** Cancel a previously-scheduled onboarding nudge (used when the user
 *  re-runs onboarding from settings, or completes the walkthrough
 *  before 24h elapses and we want to suppress the ping). */
export async function cancelPostOnboardingNudge(
  propertyId: string
): Promise<void> {
  if (!NOTIFICATIONS_AVAILABLE) return;
  const identifier = `homestead-onboarding-tune-${propertyId}`;
  await Notifications.cancelScheduledNotificationAsync(identifier).catch(() => {});
}

export async function requestNotificationPermissionUnified(): Promise<NotificationPermissionStatus> {
  if (Platform.OS === 'web') {
    const ok = await requestWebNotificationPermission();
    if (ok) return 'granted';
    // eslint-disable-next-line no-undef
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return 'denied';
    return WEB_NOTIFICATIONS_AVAILABLE ? 'not-requested' : 'unavailable';
  }
  const settings = await Notifications.getPermissionsAsync();
  if (settings.granted) return 'granted';
  if (settings.canAskAgain === false) return 'denied';
  const res = await Notifications.requestPermissionsAsync();
  if (res.granted) return 'granted';
  if (res.canAskAgain === false) return 'denied';
  return 'not-requested';
}

/**
 * Show a notification immediately on web. Prefers the service-worker
 * path (`registration.showNotification`) so the notification persists in
 * the OS notification center and survives the tab being backgrounded.
 * Falls back to the constructor form when no SW is registered.
 */
export async function showImmediateWebNotification(
  title: string,
  body: string,
  url?: string
): Promise<boolean> {
  if (!WEB_NOTIFICATIONS_AVAILABLE) return false;
  const granted = await requestWebNotificationPermission();
  if (!granted) return false;
  try {
    if (swRegistration) {
      await swRegistration.showNotification(title, {
        body,
        data: { url: url ?? '/' },
        icon: '/icon.png',
        badge: '/icon.png',
      });
      return true;
    }
    // eslint-disable-next-line no-undef
    new Notification(title, { body });
    return true;
  } catch {
    return false;
  }
}

const WEEKLY_ID = 'homestead-weekly-digest';
const DAILY_ID = 'homestead-daily-digest';
const MONTHLY_ID = 'homestead-monthly-digest';
const OVERDUE_ID = 'homestead-overdue-nudge';

export async function requestNotificationPermission(): Promise<boolean> {
  if (!NOTIFICATIONS_AVAILABLE) return false;
  const settings = await Notifications.getPermissionsAsync();
  if (settings.granted) return true;
  if (!settings.canAskAgain) return false;
  const res = await Notifications.requestPermissionsAsync();
  return res.granted;
}

export async function cancelAllDigests(): Promise<void> {
  if (!NOTIFICATIONS_AVAILABLE) return;
  // Legacy single-digest ids (pre-Wave-47 installs may still carry them).
  await Promise.all([
    Notifications.cancelScheduledNotificationAsync(WEEKLY_ID).catch(() => {}),
    Notifications.cancelScheduledNotificationAsync(DAILY_ID).catch(() => {}),
    Notifications.cancelScheduledNotificationAsync(MONTHLY_ID).catch(() => {}),
    Notifications.cancelScheduledNotificationAsync(OVERDUE_ID).catch(() => {}),
  ]);
  // Per-home digests + overdue nudges (Wave 47). OVERDUE_ID above also
  // matches the 'homestead-overdue-' prefix, which is fine.
  await cancelAllByPrefix('homestead-digest-');
  await cancelAllByPrefix('homestead-overdue-');
}

/**
 * Cancel every scheduled notification whose identifier starts with the
 * given prefix. Used when the user toggles a category off and we want
 * to honor the change immediately — without this, already-scheduled
 * reminders would still fire from the OS queue after the user thought
 * they'd turned them off.
 */
async function cancelAllByPrefix(prefix: string): Promise<void> {
  if (!NOTIFICATIONS_AVAILABLE) return;
  try {
    const all = await Notifications.getAllScheduledNotificationsAsync();
    await Promise.all(
      all
        .filter((n) => typeof n.identifier === 'string' && n.identifier.startsWith(prefix))
        .map((n) =>
          Notifications.cancelScheduledNotificationAsync(n.identifier).catch(() => {})
        )
    );
  } catch {
    // ignore — best-effort cleanup
  }
}

export async function cancelAllTaskReminders(): Promise<void> {
  await cancelAllByPrefix('homestead-task-');
}

export async function cancelAllSystemReminders(): Promise<void> {
  // Two id families: legacy primary reminders ('homestead-system-') and
  // the multi-reminder rows from system-reminders.ts ('homestead-sysrem-').
  // The settings toggle must silence BOTH or "paused" is a lie.
  await cancelAllByPrefix('homestead-system-');
  await cancelAllByPrefix('homestead-sysrem-');
}

/**
 * Shift an hour-of-day out of the quiet window. If `hour` falls inside
 * [start, end) (window may cross midnight), returns `end` — i.e. fire
 * the moment quiet hours lift. No-op when quiet hours are unset.
 */
export function shiftOutOfQuietHours(
  hour: number,
  start: number | null | undefined,
  end: number | null | undefined
): number {
  if (start == null || end == null || start === end) return hour;
  const inWindow =
    start < end ? hour >= start && hour < end : hour >= start || hour < end;
  return inWindow ? end : hour;
}

/**
 * True when the current time (or the supplied date) falls within the
 * configured quiet-hours window. Window crosses midnight when
 * `start > end` (e.g. 22 → 7 means "10pm to 7am"). Returns false when
 * either bound is null, so callers can pass property fields directly.
 */
export function isInQuietHours(
  start: number | null | undefined,
  end: number | null | undefined,
  at: Date = new Date()
): boolean {
  if (start == null || end == null) return false;
  if (start === end) return false; // zero-width window = effectively off
  const hour = at.getHours();
  if (start < end) {
    // Same-day window, e.g. 13 → 15 means 1pm-3pm.
    return hour >= start && hour < end;
  }
  // Crosses midnight, e.g. 22 → 7 means 10pm-7am.
  return hour >= start || hour < end;
}

/**
 * Unified digest composer — the ONLY place digests get scheduled.
 *
 * Reads the active property's cadence preference (off / daily / weekly /
 * monthly + day-of-week + hour) straight from the DB, aggregates the
 * headline task, and schedules the matching repeating notification.
 * Called from app boot (useDigestRescheduler), the notification settings
 * page after any preference change, and the onboarding cadence step.
 *
 * Design invariants:
 *   1. ONE DIGEST PER HOME (Wave 47). Every non-deleted home with
 *      notifications enabled gets its own schedule on ITS OWN cadence
 *      (frequency / day / hour / quiet hours all read per-property).
 *      Previously only the ACTIVE home's cadence governed a single
 *      aggregated digest — multi-home users effectively had no control
 *      over (or delivery for) their other homes.
 *   2. EMBEDS the task title (+ home name when >1 home) in the body so
 *      the lock screen tells you what's up. A home with no due tasks
 *      schedules nothing (no "you have no tasks" buzzes).
 *   3. IDEMPOTENT — cancels the whole digest family before scheduling,
 *      so calling on every boot keeps headlines fresh without stacking.
 *   4. RESPECTS each home's stored frequency, and quiet hours beat the
 *      chosen hour (inside-window hours slide to when the window lifts).
 *   5. Daily/weekly cadences also refresh that home's overdue nudge
 *      (when notify_overdue is on) with a live count. Monthly homes
 *      asked for exactly one touch a month — no extra nudge.
 */
export async function composeAndScheduleDigest(
  db: SQLiteDatabase
): Promise<{ scheduled: boolean; homesScheduled?: number }> {
  if (!NOTIFICATIONS_AVAILABLE) return { scheduled: false };

  // Rebuild the whole digest family from scratch each pass (also clears
  // schedules for homes that were deleted or turned off since last run).
  await cancelAllDigests();

  // No OS permission → nothing would be delivered anyway. Bail without
  // scheduling so we never queue pings the user hasn't opted into at
  // the OS level.
  const perm = await Notifications.getPermissionsAsync();
  if (!perm.granted) return { scheduled: false };

  const props = await db.getAllAsync<{
    id: string;
    name: string | null;
    enabled: number | null;
    frequency: string | null;
    day: number | null;
    hour: number | null;
    overdue: number | null;
    quiet_start: number | null;
    quiet_end: number | null;
  }>(
    `SELECT id, name,
            notifications_enabled AS enabled,
            notification_frequency AS frequency,
            notification_day AS day,
            notification_hour AS hour,
            notify_overdue AS overdue,
            quiet_hours_start AS quiet_start,
            quiet_hours_end AS quiet_end
     FROM property WHERE deleted_at IS NULL ORDER BY created_at ASC`
  );
  if (props.length === 0) return { scheduled: false };

  await ensureAndroidNotificationChannel();
  const multiHome = props.length > 1;
  let homesScheduled = 0;

  for (const p of props) {
    const frequency = (p.frequency ?? 'weekly') as
      | 'off'
      | 'daily'
      | 'weekly'
      | 'monthly';
    if ((p.enabled ?? 1) === 0 || frequency === 'off') continue;

    const hour = shiftOutOfQuietHours(p.hour ?? 8, p.quiet_start, p.quiet_end);
    const weekday = (p.day ?? 1) + 1; // DB 0-6 (0=Sun) → Expo 1-7 (1=Sun)

    // Headline: this home's earliest-due open task.
    const rows = await db.getAllAsync<{ title: string | null; due_at: number | null }>(
      `SELECT title, due_at FROM task
       WHERE property_id = ? AND completed_at IS NULL AND deleted_at IS NULL
         AND due_at IS NOT NULL
       ORDER BY due_at ASC
       LIMIT 5`,
      p.id
    );
    const best = rows.find((t) => !!t.title && t.due_at != null);
    if (!best) continue; // nothing to say for this home — schedule nothing

    const homeName = p.name?.trim() || 'your home';
    const homeSuffix = multiHome ? ` · ${homeName}` : '';
    const moreSuffix = rows.length > 1 ? `  (+${rows.length - 1} more)` : '';
    const body = `${best.title}${homeSuffix}${moreSuffix}`;
    const identifier = `homestead-digest-${p.id}`;

    if (frequency === 'daily') {
      await Notifications.scheduleNotificationAsync({
        identifier,
        content: { title: 'Homestead — today', body, sound: 'default' },
        trigger: {
          type: Notifications.SchedulableTriggerInputTypes.DAILY,
          hour,
          minute: 0,
        },
      });
    } else if (frequency === 'weekly') {
      await Notifications.scheduleNotificationAsync({
        identifier,
        content: {
          title: 'Homestead — your one thing this week',
          body: `Up next: ${body}`,
          sound: 'default',
        },
        trigger: {
          type: Notifications.SchedulableTriggerInputTypes.WEEKLY,
          weekday,
          hour,
          minute: 0,
        },
      });
    } else {
      // Monthly — a single check-in on the 1st at the chosen hour.
      await Notifications.scheduleNotificationAsync({
        identifier,
        content: {
          title: 'Homestead — monthly check-in',
          body: `Up next: ${body}`,
          sound: 'default',
        },
        trigger: {
          type: Notifications.SchedulableTriggerInputTypes.MONTHLY,
          day: 1,
          hour,
          minute: 0,
        },
      });
    }
    homesScheduled += 1;

    // Refresh this home's overdue nudge with a live count so it never
    // repeats a stale number.
    if ((p.overdue ?? 1) === 1 && frequency !== 'monthly') {
      const overdueRow = await db.getFirstAsync<{ n: number }>(
        `SELECT COUNT(*) AS n FROM task
         WHERE property_id = ? AND completed_at IS NULL AND deleted_at IS NULL
           AND due_at IS NOT NULL AND due_at < ?`,
        p.id,
        Date.now()
      );
      const overdueCount = overdueRow?.n ?? 0;
      if (overdueCount > 0) {
        // A few hours after the digest, shifted out of the quiet window
        // (an 8 PM digest would otherwise nudge at 2 AM).
        const nudgeHour = shiftOutOfQuietHours(
          (hour + 6) % 24,
          p.quiet_start,
          p.quiet_end
        );
        await scheduleOverdueNudge(p.id, weekday, nudgeHour, overdueCount, multiHome ? homeName : null);
      }
    }
  }

  return { scheduled: homesScheduled > 0, homesScheduled };
}

/**
 * Once-a-week "you've got N overdue" nudge, per home (Wave 47). Fires on
 * the SAME day as that home's digest but a few hours later so it lands
 * as a check-in.
 */
export async function scheduleOverdueNudge(
  /** Home this nudge belongs to — keys the notification identifier so
   *  each home's nudge coexists in the OS queue. */
  propertyId: string,
  weekday: number,
  /** Exact hour to fire (0-23). Callers pick an hour outside the quiet
   *  window — see composeAndScheduleDigest, which derives digest hour
   *  + 6 and then shifts out of quiet hours. */
  hour: number,
  overdueCount: number,
  /** Home name to embed in the body — pass null for single-home users. */
  homeName?: string | null
): Promise<string | null> {
  if (!NOTIFICATIONS_AVAILABLE || overdueCount <= 0) return null;
  const identifier = `homestead-overdue-${propertyId}`;
  await Notifications.cancelScheduledNotificationAsync(identifier).catch(() => {});
  await ensureAndroidNotificationChannel();
  const suffix = homeName ? ` at ${homeName}` : '';
  return await Notifications.scheduleNotificationAsync({
    identifier,
    content: {
      title: 'Homestead — a gentle nudge',
      body:
        overdueCount === 1
          ? `One thing${suffix} has been waiting a bit. Quick check?`
          : `${overdueCount} things${suffix} have been waiting. Sweep them in a minute or two?`,
      sound: 'default',
    },
    trigger: {
      type: Notifications.SchedulableTriggerInputTypes.WEEKLY,
      weekday,
      hour,
      minute: 0,
    },
  });
}

/**
 * Per-task reminder. Schedules a one-shot local notification at the chosen
 * lead time (defaults to 1 day before due). Returns the notification id so
 * the caller can cancel it later when the task is rescheduled / completed.
 */
/**
 * Schedule a task reminder. Two delivery paths:
 *   - Native (iOS/Android): expo-notifications schedules an OS-level
 *     notification fired at the lead-day mark even if the app is closed.
 *   - Web: browsers can't reliably schedule background notifications, so
 *     we persist the reminder_at / reminder_lead_days on the task row.
 *     A focus-checker (`fireDueWebReminders` below) scans on every app
 *     boot + focus and fires `showImmediateWebNotification` for any
 *     reminders whose fire time has passed and haven't been notified yet.
 *
 * Returns the OS notification id on native; a sentinel `'web-deferred'`
 * on web to communicate that the reminder was accepted but will fire on
 * next app open. Returns null when scheduling can't proceed (no
 * permission, fire time in the past, etc.).
 */
export async function scheduleTaskReminder(
  taskId: string,
  taskTitle: string,
  due_at: number,
  leadDays: 1 | 7 = 1,
  /** Property id of the home this task belongs to. Embedded in the
   *  notification payload so the tap handler can auto-switch the active
   *  home before navigating — critical for multi-home: tapping a Lake
   *  House reminder while sitting on Main House should land you ON the
   *  task on Lake House, not on a deep-link that 404s because the task
   *  belongs to a different property. Optional for callers that haven't
   *  been updated yet; the listener falls back to a plain navigate. */
  propertyId?: string | null
): Promise<string | null> {
  const fireAt = due_at - leadDays * 24 * 60 * 60 * 1000;
  if (fireAt <= Date.now()) return null; // can't schedule in the past

  if (NOTIFICATIONS_AVAILABLE) {
    // Native — schedule with the OS so it fires even when the app is
    // closed. Existing reminder for this task gets cancelled first so
    // changes to lead time don't double-schedule.
    const granted = await requestNotificationPermission();
    if (!granted) return null;
    const identifier = `homestead-task-${taskId}`;
    await Notifications.cancelScheduledNotificationAsync(identifier).catch(() => {});
    await ensureAndroidNotificationChannel();
    return await Notifications.scheduleNotificationAsync({
      identifier,
      content: {
        title: leadDays === 1 ? 'Due tomorrow' : 'Due next week',
        body: taskTitle,
        data: {
          taskId,
          kind: 'task-reminder',
          propertyId: propertyId ?? null,
          route: `/task/${taskId}`,
        },
      },
      trigger: {
        type: Notifications.SchedulableTriggerInputTypes.DATE,
        date: new Date(fireAt),
      },
    });
  }

  if (WEB_NOTIFICATIONS_AVAILABLE) {
    // Web — request permission, but don't fail if denied (the caller
    // will persist reminder_at regardless; an in-app toast on the home
    // screen can still surface the reminder when the user opens the
    // app even with no OS-level notification).
    await requestWebNotificationPermission().catch(() => false);
    return 'web-deferred';
  }

  return null;
}

export async function cancelTaskReminder(taskId: string): Promise<void> {
  if (NOTIFICATIONS_AVAILABLE) {
    const identifier = `homestead-task-${taskId}`;
    await Notifications.cancelScheduledNotificationAsync(identifier).catch(() => {});
  }
  // On web there's nothing to cancel at the OS layer — the focus checker
  // reads reminder_at from the task row, so clearing the row is the
  // caller's responsibility (and that's what they're about to do).
}

/**
 * Web fallback delivery: scan task rows whose reminder_at has passed
 * but reminder_notified_at is unset, fire a notification for each,
 * mark them notified. Called from AppGlobals on focus + boot.
 *
 * The window is intentionally bounded to the last 7 days so a user who
 * comes back after a long absence doesn't get spammed with a backlog
 * of stale reminders — anything older is silently marked notified.
 *
 * Native is a no-op (the OS already delivered the scheduled notification).
 * If the user runs both, native fires first; the focus checker sees
 * notified_at is unset and would re-fire — to avoid double-pinging,
 * we ONLY run this on web.
 */
export async function fireDueWebReminders(
  db: SQLiteDatabase
): Promise<number> {
  if (Platform.OS !== 'web') return 0;
  const nowMs = Date.now();
  const WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
  const cutoff = nowMs - WINDOW_MS;

  const rows = await db.getAllAsync<{
    id: string;
    title: string;
    reminder_at: number;
  }>(
    `SELECT id, title, reminder_at FROM task
       WHERE deleted_at IS NULL
         AND completed_at IS NULL
         AND reminder_at IS NOT NULL
         AND reminder_at <= ?
         AND reminder_notified_at IS NULL
         AND property_id IN (SELECT id FROM property WHERE deleted_at IS NULL)`,
    nowMs
  );
  if (rows.length === 0) return 0;

  let fired = 0;
  for (const r of rows) {
    const stale = r.reminder_at < cutoff;
    if (!stale) {
      // Try to deliver. Best-effort; if showImmediateWebNotification
      // returns false (no permission, no service worker), we still
      // mark notified so the next focus tick doesn't keep retrying.
      void showImmediateWebNotification(
        'Task reminder',
        r.title,
        `/task/${r.id}`
      );
      fired += 1;
    }
    await db.runAsync(
      `UPDATE task SET reminder_notified_at = ?, updated_at = ? WHERE id = ?`,
      nowMs,
      nowMs,
      r.id
    );
  }
  return fired;
}

/**
 * Per-system standalone reminder. Distinct from task reminders — this is
 * for things like "insurance renewal", "warranty expires", "service
 * contract end" where the date is annual / one-shot rather than recurring.
 */
export async function scheduleSystemReminder(
  systemId: string,
  label: string,
  fireAt: number
): Promise<string | null> {
  if (!NOTIFICATIONS_AVAILABLE) return null;
  if (fireAt <= Date.now()) return null;
  const granted = await requestNotificationPermission();
  if (!granted) return null;
  const identifier = `homestead-system-${systemId}`;
  await Notifications.cancelScheduledNotificationAsync(identifier).catch(() => {});
  await ensureAndroidNotificationChannel();
  return await Notifications.scheduleNotificationAsync({
    identifier,
    content: {
      title: 'System reminder',
      body: label,
      data: { systemId, kind: 'system-reminder' },
    },
    trigger: {
      type: Notifications.SchedulableTriggerInputTypes.DATE,
      date: new Date(fireAt),
    },
  });
}

export async function cancelSystemReminder(systemId: string): Promise<void> {
  if (!NOTIFICATIONS_AVAILABLE) return;
  const identifier = `homestead-system-${systemId}`;
  await Notifications.cancelScheduledNotificationAsync(identifier).catch(() => {});
}

/**
 * Check whether a task currently has a reminder scheduled. Returns the
 * lead time in days if found so the UI can render the matching toggle
 * state without persisting it in the DB (the OS owns the truth).
 */
export async function getScheduledTaskReminder(
  taskId: string,
  due_at: number
): Promise<{ leadDays: 1 | 7 } | null> {
  if (!NOTIFICATIONS_AVAILABLE) return null;
  const identifier = `homestead-task-${taskId}`;
  const all = await Notifications.getAllScheduledNotificationsAsync();
  const match = all.find((n) => n.identifier === identifier);
  if (!match) return null;
  // Infer lead time from the gap between scheduled trigger and due date.
  const trigger = match.trigger as { date?: number | string | Date } | null;
  const triggerMs =
    trigger?.date instanceof Date
      ? trigger.date.getTime()
      : typeof trigger?.date === 'number'
      ? trigger.date
      : typeof trigger?.date === 'string'
      ? Date.parse(trigger.date)
      : null;
  if (triggerMs == null) return null;
  const gap = Math.round((due_at - triggerMs) / (24 * 60 * 60 * 1000));
  return { leadDays: gap >= 4 ? 7 : 1 };
}

export function dayLabel(weekday: number): string {
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][weekday - 1] ?? '?';
}
